import React from 'react';
import type { PortfolioType } from '../types';

// Search by sender emails - official CAS statement sources
const CAS_SENDERS = {
  'CAMS_KFINTECH': ['donotreply@camsonline.com', 'samfs@kfintech.com'],
  'CDSL': ['ecas@cdslstatement.com'],
  'NSDL': ['nsdl-cas@nsdl.co.in'],
};

// Build search query for sender emails
const buildSearchQuery = (type?: PortfolioType) => {
  const senders = type ? CAS_SENDERS[type] : Object.values(CAS_SENDERS).flat();
  const fromQuery = senders.map(s => `from:${s}`).join(' OR ');
  return `(${fromQuery}) has:attachment`;
};

// Official brand logos as inline SVG
const GmailLogo = () => (
  <svg width="20" height="16" viewBox="0 0 24 18" fill="none">
    <path d="M1.636 18H5.455V8.682L0 4.91V16.364C0 17.267.733 18 1.636 18z" fill="#4285F4"/>
    <path d="M18.545 18h3.819c.904 0 1.636-.732 1.636-1.636V4.91l-5.455 3.772V18z" fill="#34A853"/>
    <path d="M18.545 1.636V8.682L24 4.91v-1.91c0-2.022-2.309-3.175-3.927-1.963l-1.528 1.109z" fill="#FBBC04"/>
    <path d="M5.455 8.682V1.636l6.545 4.91 6.545-4.91v7.046L12 13.592 5.455 8.682z" fill="#EA4335"/>
    <path d="M0 2.999v1.91l5.455 3.773V1.636L3.927.527C2.31-.685 0 .468 0 2.999z" fill="#C5221F"/>
  </svg>
);

const OutlookLogo = () => (
  <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
    <path d="M19.484 7.937v5.477l1.916 1.205a.489.489 0 00.21.063.489.489 0 00.229-.063l8.088-5.234a1.455 1.455 0 00-.741-1.448H19.484z" fill="#0072C6"/>
    <path d="M19.484 15.457l1.747 1.2a.522.522 0 00.543 0c-.3.181 8.073-5.378 8.073-5.378v10.795a1.474 1.474 0 01-1.474 1.474h-8.889V15.457z" fill="#0072C6"/>
    <path d="M10.44 12.932a1.927 1.927 0 00-1.384-.543 1.9 1.9 0 00-1.387.543 1.927 1.927 0 00-.543 1.384v3.086a1.927 1.927 0 00.543 1.384 1.864 1.864 0 001.384.543 1.927 1.927 0 001.387-.543 1.9 1.9 0 00.543-1.384v-3.086a1.927 1.927 0 00-.543-1.384z" fill="#0072C6"/>
    <path d="M18.044 5.116H8.044a1.474 1.474 0 00-1.474 1.474v1.209l6.3 4.5 5.174-3.15V6.59a1.474 1.474 0 00-1.474-1.474H8.044z" fill="#0072C6"/>
    <path d="M1.474 9.789h16.842v14.737H1.474A1.474 1.474 0 010 23.053V11.263a1.474 1.474 0 011.474-1.474z" fill="#0072C6"/>
    <ellipse cx="9.263" cy="16.842" rx="3.789" ry="4.421" stroke="#fff" strokeWidth="1.5" fill="none"/>
  </svg>
);

const YahooLogo = () => (
  <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
    <path d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2z" fill="#5C0DAC"/>
    <path d="M22.287 10.5h-2.844l-3.118 6.066L13.178 10.5h-2.89l4.622 8.37v4.63h2.666v-4.63l4.711-8.37z" fill="#fff"/>
  </svg>
);

// Generate provider URLs based on selected CAS type
const getProviders = (selectedType?: PortfolioType) => {
  const query = buildSearchQuery(selectedType);
  const encodedQuery = encodeURIComponent(query);
  
  return [
    {
      name: 'Gmail',
      Logo: GmailLogo,
      url: `https://mail.google.com/mail/u/0/#search/${encodedQuery}`
    },
    {
      name: 'Outlook',
      Logo: OutlookLogo,
      url: `https://outlook.live.com/mail/0/deeplink?version=20200615005&search=${encodedQuery}`
    },
    {
      name: 'Yahoo',
      Logo: YahooLogo,
      url: `https://mail.yahoo.com/d/search/keyword=${encodedQuery}`
    }
  ];
};

interface ShortcutLinksProps {
  selectedType?: PortfolioType;
  onSearchClick?: (provider: string) => void;
}

export const ShortcutLinks: React.FC<ShortcutLinksProps> = ({ selectedType, onSearchClick }) => {
  const providers = getProviders(selectedType);
  
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ 
        fontSize: 12, 
        color: 'var(--pc-color-text-muted)', 
        marginBottom: 10,
        fontWeight: 500 
      }}>
        Find statement in your inbox
      </div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: 10 
      }}>
        {providers.map((provider) => (
          <a
            key={provider.name}
            href={provider.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onSearchClick?.(provider.name)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 10,
              backgroundColor: 'var(--pc-color-surface-subtle)',
              borderRadius: 'var(--pc-radius-md)',
              textDecoration: 'none',
              border: '1px solid var(--pc-color-border)',
              transition: 'all 0.15s',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pc-color-primary-soft)';
              e.currentTarget.style.borderColor = 'var(--pc-color-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pc-color-surface-subtle)';
              e.currentTarget.style.borderColor = 'var(--pc-color-border)';
            }}
          >
            <provider.Logo />
            <span style={{ fontSize: 11, color: 'var(--pc-color-text)', fontWeight: 500, marginTop: 4 }}>
              {provider.name}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};
